import React, { useState, useEffect } from 'react';
import { Printer, Wifi, Usb, Save, TestTube, Plus, Trash2, Star, Settings, Info, Edit2, X, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { val: 'all', label: 'Tout (cuisine + client)', color: '#0A422D' },
  { val: 'kitchen', label: 'Cuisine uniquement', color: '#E16833' },
  { val: 'receipt', label: 'Tickets clients', color: '#D4A853' },
  { val: 'bar', label: 'Bar', color: '#3B82F6' },
];

const emptyPrinter = {
  name: '', brand: '', model: '', connection_type: 'browser',
  ip_address: '', port: 9100, paper_width: '80mm', role: 'all',
  auto_print_kitchen: false, auto_print_receipt: false,
  copies_kitchen: 1, copies_receipt: 1, is_default: false,
};

export const PrinterTab = () => {
  const [printers, setPrinters] = useState([]);
  const [knownPrinters, setKnownPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPrinter, setEditPrinter] = useState(null); // null=list, object=editing
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');

  const fetchData = async () => {
    try {
      const [listRes, modelsRes] = await Promise.all([
        axios.get(`${API}/admin/printer/list`),
        axios.get(`${API}/admin/printer/known-models`),
      ]);
      setPrinters(listRes.data);
      setKnownPrinters(modelsRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const startAdd = () => { setEditPrinter({ ...emptyPrinter }); setIsNew(true); setSelectedBrand(''); };
  const startEdit = (p) => { setEditPrinter({ ...p }); setIsNew(false); setSelectedBrand(p.brand || ''); };
  const cancelEdit = () => { setEditPrinter(null); setIsNew(false); };

  const update = (field, value) => setEditPrinter(prev => ({ ...prev, [field]: value }));

  const savePrinter = async () => {
    if (!editPrinter.name.trim()) { toast.error('Donnez un nom a l\'imprimante'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/printer/add`, editPrinter);
        toast.success('Imprimante ajoutee');
      } else {
        await axios.put(`${API}/admin/printer/update/${editPrinter.id}`, editPrinter);
        toast.success('Imprimante mise a jour');
      }
      await fetchData();
      setEditPrinter(null); setIsNew(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deletePrinter = async (id) => {
    try {
      await axios.delete(`${API}/admin/printer/delete/${id}`);
      toast.success('Imprimante supprimee');
      await fetchData();
      if (editPrinter?.id === id) { setEditPrinter(null); setIsNew(false); }
    } catch { toast.error('Erreur'); }
  };

  const testPrint = (printer) => {
    const w = window.open('', '_blank', 'width=350,height=600');
    if (!w) return;
    const lines = [
      '================================',
      '    TEST IMPRIMANTE',
      '================================',
      '',
      `Nom: ${printer.name}`,
      `Modele: ${printer.brand} ${printer.model}`,
      `Role: ${ROLES.find(r => r.val === printer.role)?.label || printer.role}`,
      `Connexion: ${printer.connection_type}`,
      `Largeur: ${printer.paper_width}`,
      '',
      '1234567890123456789012345678901234567890',
      '',
      '================================',
      '       FIN DU TEST',
      '================================',
    ].join('\n');
    w.document.write(`<!DOCTYPE html><html><head><style>
      @page{size:80mm auto;margin:0}
      body{font-family:'Courier New',monospace;font-size:12px;width:72mm;margin:0 auto;padding:4mm 2mm;white-space:pre-wrap}
    </style></head><body>${lines}
    <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div><script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
    </body></html>`);
    w.document.close();
    toast.success(`Test envoye: ${printer.name}`);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-3 border-[#0A422D]/30 border-t-[#0A422D] rounded-full animate-spin" /></div>;

  const brandModels = knownPrinters.find(p => p.brand === selectedBrand)?.models || [];

  // ─── EDIT/ADD FORM ───
  if (editPrinter) {
    return (
      <div className="space-y-4" data-testid="printer-edit">
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Printer className="w-5 h-5 text-[#E16833]" />
            <h3 className="font-bold text-[#2D241D]">{isNew ? 'Ajouter une imprimante' : `Modifier: ${editPrinter.name}`}</h3>
          </div>
          <div className="flex gap-2">
            <Button onClick={cancelEdit} variant="outline" className="rounded-xl text-xs" data-testid="printer-cancel">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={savePrinter} disabled={saving} className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl gap-2" data-testid="printer-save-btn">
              <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Identity */}
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
                <Settings className="w-4 h-4 text-[#6E5E52]" /> Identification
              </h4>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Nom de l'imprimante *</label>
                <Input value={editPrinter.name} onChange={e => update('name', e.target.value)}
                  placeholder="ex: Cuisine, Caisse, Bar..." className="rounded-lg" data-testid="printer-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Role / Usage</label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map(r => (
                    <button key={r.val} onClick={() => update('role', r.val)}
                      className={`p-2 rounded-xl border text-xs font-bold text-center transition-all active:scale-95 ${editPrinter.role === r.val
                        ? 'border-[#0A422D] bg-[#0A422D]/5 text-[#0A422D]'
                        : 'border-[#E5DCD3] text-[#6E5E52] hover:border-[#0A422D]/30'}`}
                      data-testid={`printer-role-${r.val}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => update('is_default', !editPrinter.is_default)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${editPrinter.is_default
                    ? 'border-[#D4A853] bg-[#D4A853]/10 text-[#D4A853]'
                    : 'border-[#E5DCD3] text-[#6E5E52]'}`}
                  data-testid="printer-default-toggle">
                  <Star className={`w-4 h-4 ${editPrinter.is_default ? 'fill-current' : ''}`} />
                  Imprimante par defaut
                </button>
              </div>
            </div>
          </div>

          {/* Brand / Model */}
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
                <Printer className="w-4 h-4 text-[#6E5E52]" /> Modele
              </h4>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Marque</label>
                <select value={selectedBrand} onChange={e => { setSelectedBrand(e.target.value); update('brand', e.target.value); update('model', ''); }}
                  className="w-full h-9 px-3 rounded-lg border border-[#E5DCD3] text-sm text-[#2D241D]" data-testid="printer-brand">
                  <option value="">-- Selectionnez --</option>
                  {knownPrinters.map(p => <option key={p.brand} value={p.brand}>{p.brand}</option>)}
                </select>
              </div>
              {selectedBrand && (
                <div>
                  <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Modele</label>
                  <select value={editPrinter.model} onChange={e => update('model', e.target.value)}
                    className="w-full h-9 px-3 rounded-lg border border-[#E5DCD3] text-sm text-[#2D241D]" data-testid="printer-model">
                    <option value="">-- Selectionnez --</option>
                    {brandModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Largeur papier</label>
                <select value={editPrinter.paper_width} onChange={e => update('paper_width', e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-[#E5DCD3] text-sm text-[#2D241D]" data-testid="printer-width">
                  <option value="80mm">80mm (standard)</option>
                  <option value="58mm">58mm</option>
                </select>
              </div>
            </div>
          </div>

          {/* Connection */}
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
                <Wifi className="w-4 h-4 text-[#6E5E52]" /> Connexion
              </h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: 'browser', label: 'Navigateur', icon: Printer, desc: 'window.print()' },
                  { val: 'network', label: 'Reseau (LAN)', icon: Wifi, desc: 'IP:Port' },
                  { val: 'usb', label: 'USB Direct', icon: Usb, desc: 'WebUSB API' },
                ].map(opt => (
                  <button key={opt.val} onClick={() => update('connection_type', opt.val)}
                    className={`p-3 rounded-xl border text-center transition-all active:scale-95 ${editPrinter.connection_type === opt.val
                      ? 'border-[#0A422D] bg-[#0A422D]/5'
                      : 'border-[#E5DCD3] hover:border-[#0A422D]/30'}`}
                    data-testid={`printer-conn-${opt.val}`}>
                    <opt.icon className={`w-5 h-5 mx-auto mb-1 ${editPrinter.connection_type === opt.val ? 'text-[#0A422D]' : 'text-[#6E5E52]'}`} />
                    <p className="text-xs font-bold text-[#2D241D]">{opt.label}</p>
                    <p className="text-[10px] text-[#6E5E52]">{opt.desc}</p>
                  </button>
                ))}
              </div>
              {editPrinter.connection_type === 'network' && (
                <div className="space-y-2 bg-[#FAF7F2] rounded-xl p-3">
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Adresse IP</label>
                    <Input value={editPrinter.ip_address} onChange={e => update('ip_address', e.target.value)}
                      placeholder="192.168.1.100" className="rounded-lg" data-testid="printer-ip" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Port</label>
                    <Input type="number" value={editPrinter.port} onChange={e => update('port', parseInt(e.target.value) || 9100)}
                      className="rounded-lg" data-testid="printer-port" />
                  </div>
                </div>
              )}
              {editPrinter.connection_type === 'browser' && (
                <div className="bg-[#FAF7F2] rounded-xl p-3">
                  <p className="text-xs text-[#6E5E52] flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Mode navigateur : configurez cette imprimante comme imprimante par defaut dans les parametres de votre systeme.</p>
                </div>
              )}
            </div>
          </div>

          {/* Print options */}
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <h4 className="text-sm font-bold text-[#2D241D]">Options d'impression</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2D241D]">Auto-impression bon cuisine</p>
                  <p className="text-xs text-[#6E5E52]">Imprimer automatiquement a chaque commande</p>
                </div>
                <button onClick={() => update('auto_print_kitchen', !editPrinter.auto_print_kitchen)}
                  className={`w-12 h-6 rounded-full transition-colors ${editPrinter.auto_print_kitchen ? 'bg-[#0A422D]' : 'bg-[#E5DCD3]'}`}
                  data-testid="printer-auto-kitchen">
                  <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${editPrinter.auto_print_kitchen ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2D241D]">Auto-impression ticket client</p>
                  <p className="text-xs text-[#6E5E52]">Imprimer le recu apres encaissement</p>
                </div>
                <button onClick={() => update('auto_print_receipt', !editPrinter.auto_print_receipt)}
                  className={`w-12 h-6 rounded-full transition-colors ${editPrinter.auto_print_receipt ? 'bg-[#0A422D]' : 'bg-[#E5DCD3]'}`}
                  data-testid="printer-auto-receipt">
                  <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${editPrinter.auto_print_receipt ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Copies bon cuisine</label>
                  <Input type="number" min="1" max="5" value={editPrinter.copies_kitchen}
                    onChange={e => update('copies_kitchen', parseInt(e.target.value) || 1)}
                    className="rounded-lg" data-testid="printer-copies-kitchen" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Copies ticket client</label>
                  <Input type="number" min="1" max="5" value={editPrinter.copies_receipt}
                    onChange={e => update('copies_receipt', parseInt(e.target.value) || 1)}
                    className="rounded-lg" data-testid="printer-copies-receipt" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── PRINTERS LIST ───
  return (
    <div className="space-y-4" data-testid="printer-tab">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Printer className="w-5 h-5 text-[#E16833]" />
          <div>
            <h3 className="font-bold text-[#2D241D]">Imprimantes</h3>
            <p className="text-xs text-[#6E5E52]">{printers.length} imprimante{printers.length > 1 ? 's' : ''} configuree{printers.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button onClick={startAdd} className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl gap-2" data-testid="printer-add-btn">
          <Plus className="w-4 h-4" /> Ajouter une imprimante
        </Button>
      </div>

      {printers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E5DCD3] p-12 text-center">
          <Printer className="w-12 h-12 text-[#E5DCD3] mx-auto mb-3" />
          <p className="text-[#6E5E52] text-sm mb-4">Aucune imprimante configuree</p>
          <Button onClick={startAdd} className="bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-xl gap-2">
            <Plus className="w-4 h-4" /> Configurer la premiere imprimante
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {printers.map(p => {
            const roleInfo = ROLES.find(r => r.val === p.role) || ROLES[0];
            return (
              <div key={p.id} className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-md ${p.is_default ? 'border-[#D4A853] ring-1 ring-[#D4A853]/30' : 'border-[#E5DCD3]'}`}
                data-testid={`printer-card-${p.id}`}>
                <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Printer className="w-4 h-4 text-[#E16833]" />
                    <span className="font-bold text-sm text-[#2D241D]">{p.name}</span>
                    {p.is_default && <Star className="w-3.5 h-3.5 text-[#D4A853] fill-current" />}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${roleInfo.color}15`, color: roleInfo.color }}>
                    {roleInfo.label}
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-[#6E5E52]">
                    <span className="font-medium">{p.brand || 'Marque ?'} {p.model || ''}</span>
                    <span className="text-[#E5DCD3]">|</span>
                    <span>{p.paper_width}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {p.connection_type === 'browser' && <span className="flex items-center gap-1 text-[#6E5E52]"><Printer className="w-3 h-3" /> Navigateur</span>}
                    {p.connection_type === 'network' && <span className="flex items-center gap-1 text-[#6E5E52]"><Wifi className="w-3 h-3" /> {p.ip_address}:{p.port}</span>}
                    {p.connection_type === 'usb' && <span className="flex items-center gap-1 text-[#6E5E52]"><Usb className="w-3 h-3" /> USB</span>}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {p.auto_print_kitchen && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-600 font-bold">Auto cuisine</span>}
                    {p.auto_print_receipt && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 font-bold">Auto ticket</span>}
                  </div>
                </div>
                <div className="px-4 py-2 border-t border-[#E5DCD3] flex items-center gap-2">
                  <button onClick={() => testPrint(p)}
                    className="flex-1 h-8 rounded-lg bg-[#FAF7F2] hover:bg-[#E5DCD3] text-xs font-bold text-[#2D241D] flex items-center justify-center gap-1 transition-colors active:scale-95"
                    data-testid={`printer-test-${p.id}`}>
                    <TestTube className="w-3 h-3" /> Test
                  </button>
                  <button onClick={() => startEdit(p)}
                    className="flex-1 h-8 rounded-lg bg-[#0A422D] hover:bg-[#083323] text-xs font-bold text-white flex items-center justify-center gap-1 transition-colors active:scale-95"
                    data-testid={`printer-edit-${p.id}`}>
                    <Edit2 className="w-3 h-3" /> Modifier
                  </button>
                  <button onClick={() => deletePrinter(p.id)}
                    className="h-8 w-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center transition-colors active:scale-95"
                    data-testid={`printer-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compatible printers reference */}
      <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
        <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
          <h4 className="text-sm font-bold text-[#2D241D]">Imprimantes compatibles ({knownPrinters.reduce((s, p) => s + p.models.length, 0)} modeles)</h4>
        </div>
        <div className="divide-y divide-[#F3EDE5] max-h-[250px] overflow-y-auto">
          {knownPrinters.map(brand => (
            <div key={brand.brand} className="px-4 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#2D241D]">{brand.brand}</span>
                <span className="text-[10px] bg-[#0A422D]/10 text-[#0A422D] px-2 py-0.5 rounded-full font-bold">{brand.protocol}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {brand.models.map(m => (
                  <span key={m} className="text-[10px] bg-[#F3EDE5] text-[#6E5E52] px-2 py-0.5 rounded-full">{m}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
